
import PrettyBar from "@/components/PrettyBar.vue";
import NewSidebar from "@/components/NewSidebar.vue";
import useError from "@/composables/useError";
import { useGetLogQuery, LogType } from "@/graphql/types";
import { useResult } from "@vue/apollo-composable";
import gql from "graphql-tag";
import { computed, defineComponent, reactive, ref, watch, nextTick } from "vue";
import { displayUTCTimestamp } from "@/composables/utils";

// Queries
gql`
  query GetLog($id: Int!) {
    log(id: $id) {
      ...LogParts
    }
  }
`;

export default defineComponent({
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  components: {
    NewSidebar,
    PrettyBar,
  },
  emits: ["hide"],
  setup(props, { emit }) {
    const sidebarRef = ref();
    const log = reactive<LogType>({});

    const { result, loading, error } = useGetLogQuery(
      () => ({ id: Number(props.id) }),
      () => ({
        enabled: props.id !== undefined,
        fetchPolicy: "network-only",
      })
    );

    function getLogLevelText(level?: string) {
      switch (level) {
        case "ERROR":
          return "Fejl Log";
        case "WARNING":
          return "Advarsel Log";
        case "INFO":
          return "Information Log";
        default:
          return "Log Detaljer";
      }
    }

    function parseLogJson(json: any) {
      const cleanedJson = json.replace(/\\\\"/g, '\\"');
      return JSON.parse(cleanedJson);
    }

    function formatJson(json?: any) {
      if (!json) return "";
      try {
        return JSON.stringify(parseLogJson(json), null, 2);
      } catch (e) {
        return json;
      }
    }

    function formatLogMessage(json: any, dateAsString: string) {
      if (!json) return "";
      try {
        const obj = parseLogJson(json);
        obj["Timestamp"] = displayUTCTimestamp(dateAsString, true);
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return json;
      }
    }

    function handleHideSidebar() {
      sidebarRef.value?.hide();
      emit("hide");
    }

    watch(
      () => useResult(result).value,
      (newValue) => {
        if (newValue) {
          Object.assign(log, newValue);
          nextTick(() => {
            sidebarRef.value?.show();
          });
        }
      },
      { immediate: true }
    );

    return {
      log,
      error: useError(error),
      loading,
      handleHideSidebar,
      sidebarRef,
      getLogLevelText,
      formatJson,
      displayUTCTimestamp,
      formatLogMessage,
    };
  },
});
